"use client";

import { useState, useEffect } from "react";
import Link from "next/link";
import Image from "next/image";
import { usePathname } from "next/navigation";
import { NavbarClientProps } from "@/types/components";
import MobileMenu from "./MobileMenu";
import { throttleRAF } from "@/lib/helper";

export default function AnimatedNavbar({
  footerData,
  generalData,
}: NavbarClientProps) {
  // Initialize isMobile with a value based on viewport width (SSR-safe)
  // This prevents layout shift from null -> boolean transition
  const [isMobile, setIsMobile] = useState<boolean>(() => {
    if (typeof window === "undefined") return false;
    return window.innerWidth < 1024;
  });
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const pathname = usePathname();

  const isLinkActive = (url: string) => {
    if (url === "/") {
      return pathname === "/";
    }
    return pathname.startsWith(url);
  };

  useEffect(() => {
    const checkMobile = () => {
      const mobile = window.innerWidth < 1024;
      const wasMobile = isMobile;
      setIsMobile(mobile);

      if (wasMobile === true && mobile === false && isMobileMenuOpen) {
        setIsMobileMenuOpen(false);
      }
    };

    const throttledCheckMobile = throttleRAF(checkMobile, 100);

    checkMobile();
    window.addEventListener("resize", throttledCheckMobile);

    // Apply navbar-fixed class based on current state
    if (!isMobile) {
      document.body.classList.add("navbar-fixed");
    } else {
      document.body.classList.remove("navbar-fixed");
    }

    return () => {
      window.removeEventListener("resize", throttledCheckMobile);
      document.body.classList.remove("navbar-fixed");
    };
  }, [isMobile, isMobileMenuOpen]);

  // Render desktop navbar if not mobile
  if (!isMobile) {
    return (
      <header className="fixed top-0 z-40 w-full navbar-glass mx-auto max-w-[1920px]">
        <nav className="container mx-auto px-6 flex items-center justify-between h-16">
          <div className="shrink-0">
            <Link href="/">
              {footerData.logo.src ? (
                <Image
                  src={"/assets/colored_logo.svg"}
                  alt="Revealsite Logo"
                  width={258}
                  height={32}
                  priority
                  fetchPriority="high"
                  quality={85}
                  className="w-auto h-auto"
                />
              ) : null}
            </Link>
          </div>

          <div className="lg:flex flex-1 justify-center items-center space-x-8 nav-links flex">
            {footerData.usefulLinks.links.map((link, index) => (
              <Link
                key={index}
                href={link.url}
                className={`transition-colors duration-200 hover:text-accent ${
                  isLinkActive(link.url)
                    ? "text-theme-primary font-semibold"
                    : "text-white"
                }`}
              >
                {link.text}
              </Link>
            ))}
          </div>

          {footerData.buttonLink.url && footerData.buttonLink.text && (
            <Link
              href={footerData.buttonLink.url}
              className="lg:inline-block px-6 py-3 text-center transition-all duration-300 text-sm transform translate-y-0 bg-accent text-white font-semibold rounded-lg hover:bg-orange-400 hover:scale-105 relative overflow-hidden group before:content-[''] before:absolute before:top-0 before:left-[-100%] before:w-full before:h-full before:bg-gradient-to-r before:from-transparent before:via-white/20 before:to-transparent before:transition-[left] before:duration-500 hover:before:left-[100%] inline-block"
            >
              <span className="relative z-10 transition-all duration-300 text-white group-hover:text-white">
                {footerData.buttonLink.text}
              </span>
            </Link>
          )}
        </nav>
      </header>
    );
  }

  return (
    <MobileMenu
      footerData={footerData}
      generalData={generalData}
      isMobileMenuOpen={isMobileMenuOpen}
      setIsMobileMenuOpen={setIsMobileMenuOpen}
    />
  );
}
